/**
 * Components and their instances (Figma's), pure: a component is a frame marked
 * `style.component`; an instance is an item whose `style.component` names one, drawn as a copy of
 * what the component holds, scaled to the instance's size and following every change made to
 * the component. Its own words for a part are `style["text:<part id>"]` (an override).
 */
import type { BoardPlace } from "@protocol/BoardPlace";
import type { Graph } from "@protocol/Graph";
import type { Node } from "@protocol/Node";
import { contentsOf, type BoardContent } from "../../state/board";
import { INSTANCE_DEPTH } from "./constants";

/** One part of an instance: an item or a frame of its component, placed inside the instance (its corner at 0, 0). */
export interface InstancePart {
  /** The component's item or frame. */
  id: string;
  /** The item (`null`: a frame inside the component, drawn as a box). */
  node: Node | null;
  /** Its look (a frame's style for a box). */
  style: { readonly [key in string]?: string };
  /** Its words: the instance's override, else the component's. */
  text: string;
  /** Where it sits in the instance. */
  place: BoardPlace;
  /** It is an instance itself: its own parts (at most a few deep). */
  parts: InstancePart[];
}

/** The key of an instance's own words for `part`. */
export function overrideKey(part: string): string {
  return `text:${part}`;
}

/**
 * What an instance draws: the parts of the component its `style.component` names, scaled from
 * the component's frame into `place` (the instance's), frames first, then items lowest `z` first.
 * `null` when it names no component on the board.
 */
export function instanceParts(graph: Graph, content: BoardContent, node: Node, place: BoardPlace, depth = 0): { frame: { readonly [key in string]?: string }; parts: InstancePart[] } | null {
  const id = node.style?.component;
  const component = id ? content.frames.find((f) => f.id === id) : undefined;
  if (!component || depth >= INSTANCE_DEPTH) return null;
  const box = component.place;
  const [sx, sy] = [place.w / box.w, place.h / box.h];
  const within = (p: BoardPlace): BoardPlace => ({ x: (p.x - box.x) * sx, y: (p.y - box.y) * sy, w: p.w * sx, h: p.h * sy, ...(p.rotate ? { rotate: p.rotate } : {}) });
  const { nodes, frames } = contentsOf(graph, component.id);
  const inside = new Set(nodes);
  const boxes: InstancePart[] = content.frames
    .filter((f) => frames.includes(f.id))
    .map((f) => ({ id: f.id, node: null, style: f.group.style ?? {}, text: "", place: within(f.place), parts: [] }));
  const items: InstancePart[] = content.items
    .filter((i) => inside.has(i.id))
    .map((i) => {
      const at = within(i.place);
      return { id: i.id, node: i.node, style: i.node.style ?? {}, text: node.style?.[overrideKey(i.id)] ?? i.node.name, place: at, parts: instanceParts(graph, content, i.node, at, depth + 1)?.parts ?? [] };
    });
  return { frame: component.group.style ?? {}, parts: [...boxes, ...items] };
}
